import {noop} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontClickEvent} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import heroImage from '../../../../assets/images/sandboxTip.svg';
import {InfoBubblePillVariantsEnum} from '../../../common/infoBubbles/infoBubblePill';
import {EducationTip} from '../../../educationTips/educationTip';
import {generateStepText} from '../../welcome/onboardingWelcomeHelpers';
import {SandboxEducationTipProps} from './sandboxEducationTip';

/*
 * Props.
 */

interface BaseWelcomeEducationTipProps extends SandboxEducationTipProps {
  title: ReactNode;
  stepCounter?: ReactNode;
}

/*
 * Styles.
 */

const StyledImg = styled.img`
  display: block;
  width: 380px;
  height: 142px;
`;

/*
 * Component.
 */

const BaseWelcomeEducationTip: FC<BaseWelcomeEducationTipProps> = (props) => {
  const {trackDataEvent} = useDataEventTracking();

  const onStartClick = (event: FrontClickEvent) => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.DEMO_WELCOME_START, {});
    props.onNextClick(event);
  };

  const onSkipClick = (event: FrontClickEvent) => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.DEMO_WELCOME_SKIP, {});
    props.onEndClick(event, {shouldAdvanceStep: false});
  };

  return (
    <EducationTip
      anchor={props.anchor}
      position="right-start"
      isInteractive
      isVisible={props.isVisible}
      onRequestClose={noop}
      hero={<StyledImg src={heroImage} alt="" />}
      pillType={InfoBubblePillVariantsEnum.EDUCATION}
      title={props.title}
      primaryActionLabel={<Trans id="tip-onboarding-start-start">Start</Trans>}
      onPrimaryClick={onStartClick}
      secondaryAction={{
        label: <Trans id="tip-onboarding-start-skip">I know how to Front</Trans>,
        onClick: onSkipClick,
      }}
      stepCounter={props.stepCounter}
      dataObjectName={OnboardingDataEventObject.DEMO_WELCOME}
      overlayOffset={0}
    />
  );
};

export const WelcomeEducationTipV3: FC<SandboxEducationTipProps> = (props) => (
  <BaseWelcomeEducationTip
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
    title={
      <Trans id="tip-onboarding-start-title-v3">
        Learn the basics in an interactive demo within a few minutes
      </Trans>
    }
  />
);

export const StartSandboxEducationTip: FC<SandboxEducationTipProps> = (props) => (
  <BaseWelcomeEducationTip
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
    title={
      <Trans id="tip-onboarding-start-sandbox-title">
        Now, learn Front basics in a 2-minute interactive demo in your sandbox workspace
      </Trans>
    }
    stepCounter={generateStepText(3, 3)}
  />
);
